import { reducedMotion$ } from '@schmancy/directives/reduced-motion'

/**
 * Put the cursor in the field the operator has to fix, and put that field where
 * they can read it — the top of whatever scroll region holds it.
 *
 * Both callers (the form's own submit refusal, and the summary's anchor links)
 * need the identical sequence, and the sequence is easy to get subtly wrong in
 * three separate ways, so it lives once:
 *
 * - **Order.** `focus()` performs its own implicit scroll, instantly, at
 *   `block: 'nearest'`. Left in place it fires first and moves the region, so a
 *   deliberate scroll afterwards starts from somewhere else — a jump followed
 *   by a glide. `preventScroll` suppresses it; the explicit scroll is then the
 *   only motion.
 * - **Placement.** `nearest` is what the implicit scroll already did, and it is
 *   satisfied the moment the field is anywhere on screen — which is why a
 *   refusal raised from the far end of a long scroll region moves nothing.
 *   `start` lands the field at a known place. A consumer with pinned chrome
 *   clears it with its own `scroll-margin-top`.
 * - **Motion.** Explicit on both arms. A scrollport carrying `overflowWithin()`
 *   already declares `scroll-behavior: smooth`, but one that does not would
 *   hard-jump on a scroll the operator never asked for — and CSS smoothness is
 *   not suppressed by the reduced-motion preference on its own, so honouring it
 *   has to happen here. `reducedMotion$` is live, so an OS toggle mid-session
 *   is picked up.
 *
 * The element is the field HOST, never the inner control `delegatesFocus`
 * hands focus to: the host is the box carrying the label and the validation
 * message, and scrolling the inner control to `start` pushes its label above
 * the fold, landing the operator on an unlabelled input.
 */
export function bringFieldIntoView(field: HTMLElement | undefined | null): void {
	field?.focus({ preventScroll: true })
	field?.scrollIntoView({ block: 'start', behavior: reducedMotion$.value ? 'instant' : 'smooth' })
}
